import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import type { AdminModerationShopRow } from '../../services/api/adminShopModeration';

export interface AdminShopEditValues {
  name: string;
  description: string;
  website_url: string;
  city: string;
  state: string;
  contact_url: string;
  services: string[];
  social_instagram: string;
  verification_status: AdminModerationShopRow['verification_status'];
}

interface AdminShopEditModalProps {
  shop: AdminModerationShopRow | null;
  isOpen: boolean;
  saving: boolean;
  onClose: () => void;
  onSave: (values: AdminShopEditValues) => void;
}

const VERIFICATION_OPTIONS: AdminModerationShopRow['verification_status'][] = [
  'unverified',
  'pending',
  'verified',
  'community_verified',
  'suspended',
];

function valuesFromShop(shop: AdminModerationShopRow): AdminShopEditValues {
  const social = shop.social_links ?? {};
  return {
    name: shop.name ?? '',
    description: shop.description ?? '',
    website_url: shop.website_url ?? '',
    city: shop.city ?? '',
    state: shop.state ?? '',
    contact_url: shop.contact_url ?? '',
    services: Array.isArray(shop.services) ? [...shop.services] : [],
    social_instagram: typeof social.instagram === 'string' ? social.instagram : '',
    verification_status: shop.verification_status ?? 'unverified',
  };
}

/** Admin form to edit an existing directory shop. */
export const AdminShopEditModal: React.FC<AdminShopEditModalProps> = ({
  shop,
  isOpen,
  saving,
  onClose,
  onSave,
}) => {
  const [values, setValues] = useState<AdminShopEditValues | null>(null);
  const [serviceInput, setServiceInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && shop) {
      setValues(valuesFromShop(shop));
      setServiceInput('');
      setError(null);
    }
  }, [isOpen, shop]);

  if (!shop || !values) {
    return null;
  }

  const setField = <K extends keyof AdminShopEditValues>(key: K, value: AdminShopEditValues[K]) => {
    setValues((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const addService = () => {
    const value = serviceInput.trim();
    if (!value || values.services.some((s) => s.toLowerCase() === value.toLowerCase())) return;
    setField('services', [...values.services, value]);
    setServiceInput('');
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const name = values.name.trim();
    if (!name) {
      setError('Shop name is required.');
      return;
    }
    const website = values.website_url.trim();
    const contact = values.contact_url.trim();
    if (website && !/^https?:\/\//i.test(website)) {
      setError('Website must be an http(s) URL.');
      return;
    }
    if (contact && !/^https?:\/\//i.test(contact)) {
      setError('Contact URL must be an http(s) URL.');
      return;
    }
    setError(null);
    onSave({
      ...values,
      name,
      description: values.description.trim(),
      website_url: website,
      city: values.city.trim(),
      state: values.state.trim(),
      contact_url: contact,
      social_instagram: values.social_instagram.trim(),
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => undefined : onClose}
      title="Edit shop"
      description={`Update listing details for ${shop.name}.`}
      size="lg"
      closeOnEscape={!saving}
      closeOnOverlayClick={!saving}
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        {error ? (
          <div role="alert" className="alert-error">
            {error}
          </div>
        ) : null}

        <div>
          <label htmlFor="admin-shop-name" className="input-label">
            Shop name <span className="text-orange-300">*</span>
          </label>
          <input
            id="admin-shop-name"
            type="text"
            className="input"
            value={values.name}
            onChange={(e) => setField('name', e.target.value)}
            required
            maxLength={160}
            disabled={saving}
          />
        </div>

        <div>
          <label htmlFor="admin-shop-description" className="input-label">
            Short description
          </label>
          <textarea
            id="admin-shop-description"
            className="input min-h-[72px]"
            value={values.description}
            onChange={(e) => setField('description', e.target.value)}
            rows={3}
            maxLength={2000}
            disabled={saving}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="admin-shop-city" className="input-label">
              City
            </label>
            <input
              id="admin-shop-city"
              type="text"
              className="input"
              value={values.city}
              onChange={(e) => setField('city', e.target.value)}
              maxLength={100}
              disabled={saving}
            />
          </div>
          <div>
            <label htmlFor="admin-shop-state" className="input-label">
              State / region
            </label>
            <input
              id="admin-shop-state"
              type="text"
              className="input"
              value={values.state}
              onChange={(e) => setField('state', e.target.value)}
              maxLength={100}
              disabled={saving}
            />
          </div>
        </div>

        <div>
          <label htmlFor="admin-shop-website" className="input-label">
            Website
          </label>
          <input
            id="admin-shop-website"
            type="url"
            className="input"
            value={values.website_url}
            onChange={(e) => setField('website_url', e.target.value)}
            placeholder="https://"
            maxLength={300}
            disabled={saving}
          />
        </div>

        <div>
          <label htmlFor="admin-shop-contact" className="input-label">
            Contact URL
          </label>
          <input
            id="admin-shop-contact"
            type="url"
            className="input"
            value={values.contact_url}
            onChange={(e) => setField('contact_url', e.target.value)}
            placeholder="https://"
            maxLength={300}
            disabled={saving}
          />
        </div>

        <div>
          <label htmlFor="admin-shop-instagram" className="input-label">
            Instagram
          </label>
          <input
            id="admin-shop-instagram"
            type="text"
            className="input"
            value={values.social_instagram}
            onChange={(e) => setField('social_instagram', e.target.value)}
            placeholder="@handle or profile URL"
            disabled={saving}
          />
        </div>

        <div>
          <label htmlFor="admin-shop-service" className="input-label">
            Specialties
          </label>
          <div className="flex gap-2">
            <input
              id="admin-shop-service"
              type="text"
              className="input flex-1"
              value={serviceInput}
              onChange={(e) => setServiceInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addService();
                }
              }}
              placeholder="Add a specialty and press Enter"
              disabled={saving}
            />
            <Button type="button" variant="secondary" size="sm" onClick={addService} disabled={saving}>
              Add
            </Button>
          </div>
          {values.services.length > 0 ? (
            <ul className="flex flex-wrap gap-1 mt-2 list-none">
              {values.services.map((s) => (
                <li key={s}>
                  <button
                    type="button"
                    className="badge bg-[#141414] text-gray-300 border border-white/[0.08] text-xs cursor-pointer hover:border-orange-500/40"
                    onClick={() => setField('services', values.services.filter((x) => x !== s))}
                    disabled={saving}
                    aria-label={`Remove ${s}`}
                  >
                    {s} ×
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <div>
          <label htmlFor="admin-shop-verification" className="input-label">
            Verification
          </label>
          <select
            id="admin-shop-verification"
            className="input"
            value={values.verification_status}
            onChange={(e) =>
              setField('verification_status', e.target.value as AdminShopEditValues['verification_status'])
            }
            disabled={saving}
          >
            {VERIFICATION_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={saving}>
            Save changes
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AdminShopEditModal;
